import { type FC, memo, useCallback, useEffect, useState } from "react";
import { Pressable, View } from "react-native";
import Animated, { useSharedValue, useAnimatedStyle, withTiming, useDerivedValue } from "react-native-reanimated";
import { StyleSheet, useUnistyles } from "react-native-unistyles";
import { Icon } from "../Icon";

type CheckboxProps = {
  checked: boolean;
  indeterminate?: boolean;
  onToggle: (checked: boolean, indeterminate?: boolean) => void;
  disabled?: boolean;
  error?: string;
};

const ContainerSize = {
  default: {
    width: 18,
    height: 18,
  },
};

const IconSizeStyles = {
  default: 20,
};

const BorderWidthContainer = {
  default: 1,
};

const BorderRadiusContainer = {
  default: 1.6,
};
export const Checkbox: FC<CheckboxProps> = memo(
  ({ checked, onToggle, disabled = false, indeterminate = false, error }) => {
    const scale = useSharedValue(1);
    const isChecked = useSharedValue(checked);
    const { theme } = useUnistyles();
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    // Derived values for animations
    const backgroundOpacity = useDerivedValue(() => {
      return withTiming(isChecked.value ? 1 : 0, { duration: 300 });
    });

    const checkmarkScale = useDerivedValue(() => {
      return withTiming(isChecked.value ? 1 : 0, { duration: 300 });
    });

    const checkmarkOpacity = useDerivedValue(() => {
      return withTiming(isChecked.value ? 1 : 0, { duration: 300 });
    });

    const borderColorValue = useDerivedValue(() => {
      if (isHovered) {
        return theme.colors.interactive.controls.hover_midground;
      }
      if (disabled) {
        return theme.colors.interactive.controls.disabled_background;
      }
      if (isChecked.value) {
        return theme.colors.interactive.controls.selected_midground;
      }
      if (error) {
        return theme.colors.status.error;
      }
      return theme.colors.interactive.controls.secondary_midground;
    });

    const borderColor = useDerivedValue(() => {
      return withTiming(borderColorValue.value, { duration: 100 });
    });

    const animatedContainerBackgroundStyle = useAnimatedStyle(
      () => ({
        transform: [{ scale: scale.value }],
        borderColor: borderColor.value,
        borderWidth: BorderWidthContainer.default,
        borderRadius: BorderRadiusContainer.default,
        borderCurve: "continuous",
        width: ContainerSize.default.width,
        height: ContainerSize.default.height,
        position: "absolute",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: theme.colors.interactive.controls.default_background,
      }),
      [theme.colors.interactive.controls.default_background],
    );

    const animatedBackgroundStyle = useAnimatedStyle(() => ({
      opacity: backgroundOpacity.value,
    }));

    const animatedCheckmarkStyle = useAnimatedStyle(() => ({
      transform: [{ scale: checkmarkScale.value }],
      opacity: checkmarkOpacity.value,
    }));

    const handlePress = useCallback(() => {
      if (!disabled) {
        onToggle(!checked, indeterminate);
      }
    }, [checked, onToggle, disabled, indeterminate]);

    useEffect(() => {
      isChecked.value = checked;
    }, [checked, isChecked]);

    return (
      <View style={isFocused && styles.focused}>
        <Pressable
          onPress={handlePress}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onHoverIn={() => setIsHovered(true)}
          onHoverOut={() => setIsHovered(false)}
          style={() => [styles.container, disabled && styles.disabled]}
        >
          <Animated.View style={animatedContainerBackgroundStyle}>
            {/* Animated background */}
            <Animated.View
              style={[
                styles.checkedBackground,
                animatedBackgroundStyle,
                error && styles.errorBackground,
                disabled && styles.disabled,
              ]}
            />
            {/* Animated checkmark */}
            <Animated.View style={animatedCheckmarkStyle}>
              {indeterminate ?
                <Icon name="minus" size={12} color={theme.colors.white.default} />
              : <Icon name="tick" size={IconSizeStyles.default} color={theme.colors.white.default} />}
            </Animated.View>
          </Animated.View>
        </Pressable>
      </View>
    );
  },
);

const styles = StyleSheet.create((theme) => ({
  container: {
    width: ContainerSize.default.width,
    height: ContainerSize.default.height,
    borderColor: "transparent",
    justifyContent: "center",
    alignItems: "center",
    opacity: 1,
  },
  checkedBackground: {
    width: ContainerSize.default.width,
    height: ContainerSize.default.height,
    position: "absolute",
    top: -BorderWidthContainer.default,
    left: -BorderWidthContainer.default,
    borderRadius: BorderRadiusContainer.default,
    borderCurve: "continuous",
    backgroundColor: theme.colors.interactive.controls.selected_midground,
    borderWidth: BorderWidthContainer.default,
    borderColor: theme.colors.interactive.controls.selected_midground,
  },
  errorBackground: {
    backgroundColor: theme.colors.status.error,
    borderColor: theme.colors.status.error,
  },
  disabled: {
    backgroundColor: theme.colors.interactive.controls.disabled_background,
    borderColor: theme.colors.interactive.controls.disabled_background,
  },
  focused: {
    borderColor: theme.colors.interactive.controls.focused_border,
    borderWidth: 2,
    borderRadius: 4,
    width: ContainerSize.default.width + BorderWidthContainer.default * 2 + theme.spacing.xxs,
    height: ContainerSize.default.height + BorderWidthContainer.default * 2 + theme.spacing.xxs,
    justifyContent: "center",
    alignItems: "center",
  },
}));
